import { Link } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, Tooltip, CartesianGrid, ReferenceLine } from 'recharts';
import { ChartCard } from './charts';
import { useQuery } from '../hooks/useQuery';
import { api } from '../lib/api';
import { fmtCurrencyWhole, fmtDateTime } from '../lib/fmt';
import type { VarianceSummary } from '../lib/fpaVarianceSummary';

const fav = (n: number) => n > 0 ? 'var(--color-green-text)' : n < 0 ? 'var(--color-red-text)' : 'var(--color-text-hint)';

/** Answer-first band: the operating-profit headline vs plan + the drivers that
 *  explain the gap, in plain language. Lead every report with this. */
export function AnswerBand({ summary, baselineName, periodLabel }: { summary: VarianceSummary; baselineName: string; periodLabel: string }) {
  const top = summary.drivers.filter((d) => Math.abs(d.favorable) >= 1).slice(0, 3);
  return (
    <div style={{ padding: '12px 16px', background: 'var(--color-surface)', border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Operating profit · {periodLabel}</span>
        <span style={{ fontSize: 20, fontWeight: 600 }}>{fmtCurrencyWhole(summary.opActual)}</span>
        {summary.hasBaseline && (
          <span style={{ fontSize: 13, color: fav(summary.opDelta) }}>
            {summary.opDelta >= 0 ? '▲' : '▼'} {fmtCurrencyWhole(Math.abs(summary.opDelta))} vs {baselineName}
          </span>
        )}
      </div>
      {summary.hasBaseline && top.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
          <span style={{ color: 'var(--color-text-muted)' }}>because</span>
          {top.map((d) => {
            const raw = d.actual - d.plan;
            return (
              <span key={d.category} style={{ padding: '2px 8px', borderRadius: 4, background: 'var(--color-bg)', border: '0.5px solid var(--color-border)' }}>
                {d.label} <strong style={{ color: fav(d.favorable) }}>{fmtCurrencyWhole(Math.abs(raw))} {raw > 0 ? 'over' : 'under'}</strong>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Operating-profit variance bridge: baseline OP → ± each driver → actual OP. */
export function VarianceWaterfall({ summary, baselineName }: { summary: VarianceSummary; baselineName: string }) {
  if (!summary.hasBaseline) return null;
  let running = summary.opPlan;
  type Step = { name: string; base: number; value: number; fill: string; delta?: number };
  const steps: Step[] = [{ name: `${baselineName}`, base: Math.min(0, summary.opPlan), value: Math.abs(summary.opPlan), fill: 'var(--color-text-muted)' }];
  for (const d of summary.drivers) {
    const end = running + d.favorable;
    steps.push({ name: d.label, base: Math.min(running, end), value: Math.abs(d.favorable), fill: d.favorable >= 0 ? 'var(--color-green)' : 'var(--color-red)', delta: d.favorable });
    running = end;
  }
  steps.push({ name: 'Actual', base: Math.min(0, summary.opActual), value: Math.abs(summary.opActual), fill: 'var(--color-blue)' });

  return (
    <ChartCard title="Operating profit — plan to actual" sub="each driver's contribution">
      <ResponsiveContainer>
        <BarChart data={steps} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} interval={0} />
          <YAxis tickFormatter={(v) => fmtCurrencyWhole(v)} tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }} width={64} />
          <ReferenceLine y={0} stroke="var(--color-border-strong)" />
          <Tooltip formatter={(_v, _n, p) => { const s = p?.payload as Step; return [s?.delta != null ? `${s.delta >= 0 ? '+' : ''}${fmtCurrencyWhole(s.delta)}` : fmtCurrencyWhole(s.base + s.value), s?.name ?? '']; }} />
          <Bar dataKey="base" stackId="w" fill="transparent" />
          <Bar dataKey="value" stackId="w" radius={[2, 2, 0, 0]}>
            {steps.map((s, i) => <Cell key={i} fill={s.fill} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

/** Data-health chip: mapping gaps + sync freshness, the silent number-distorters.
 *  Green when clean; amber with a link to the QBO mapping page otherwise. */
export function DataHealthChip() {
  const def = (() => { const d = new Date(); const to = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; const s = new Date(d.getFullYear(), d.getMonth() - 11, 1); return { from: `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}`, to }; })();
  const { data } = useQuery(() => api.fpa.actuals(def.from, def.to), []);
  if (!data) return null;
  const gaps = data.unmapped.accounts.length + data.unmapped.classes.length;
  const synced = data.freshness.last_synced_at;
  const stale = synced ? (Date.now() - new Date(synced).getTime()) > 36 * 3600 * 1000 : true; // >36h
  const ok = gaps === 0 && !stale;
  const body = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '4px 8px', borderRadius: 4, background: ok ? 'var(--color-green-bg)' : 'var(--color-amber-bg)', color: ok ? 'var(--color-green-text)' : 'var(--color-amber-text)' }}>
      <span>{ok ? '●' : '▲'}</span>
      {gaps > 0 ? `${gaps} mapping gap${gaps === 1 ? '' : 's'}` : 'Data healthy'}
      <span style={{ opacity: 0.7 }}>· {synced ? `synced ${fmtDateTime(synced)}` : 'never synced'}</span>
    </span>
  );
  return gaps > 0 ? <Link to="/admin/fpa/qbo" title="Resolve mapping gaps" style={{ textDecoration: 'none' }}>{body}</Link> : body;
}
